import { WarmupSpeed } from "@/hooks/use-mailbox";
import { AssignedLabel } from "./label.types";

export enum TargetAudience {
  B2B = "b2b",
  B2C = "b2c",
  BOTH = "both",
}

export enum AuthProvider {
  AWS_SMTP = "aws_smtp",
  AWS_API = "aws_api",
  SENDGRID_SMTP = "sendgrid_smtp",
  SENDGRID_API = "sendgrid_api",
  GOOGLE = "google",
  PRIVATE = "smtp",
  MICROSOFT = "microsoft",
  AZURE = "azure",
}

export interface MailboxType {
  _id: string;
  statusLabel: string;
  totalgmailrecived: number;
  totaloutlookrecived: number;
  totalworkspacerecived: number;
  totalmicrosoftrecived: number;
  openPercentages: {
    inboxGmail : number;
    inboxOutlook : number;
    spamGmail : number;
    spamOutlook : number;
    inboxYahoo : number;
    spamYahoo : number;
    inboxZoho : number;
    spamZoho : number;
  };
  deliveryRate: number;
  email: string;
  firstName: string;
  lastName: string;
  smtpHost: string;
  smtpPort: number;
  smtpTls: boolean;
  smtpPassword: string;
  createdAt: string;
  updatedAt: string;
  todayClicksGmail: number;
  totalClicksGmail: number;
  todayRepliesGmail: number;
  totalRepliesGmail: number;
  totalzohorecived: number;
  todayClicksOutlook: number;
  totalClicksOutlook: number;
  todayClicksYahoo: number;
  totalClicksYahoo: number;
  todayClicksWorkspace: number;
  totalClicksWorkspace: number;
  todayClicksMicrosoft: number;
  totalClicksMicrosoft: number;
  todayYahooReplies: number;
  totalYahooReplies: number;
  companyId: string;
  imapHost: string;
  imapPort: number;
  imapTls: boolean;
  imapPassword: string;
  isWarmupEnabled: boolean;
  isActive: boolean;
  labels: AssignedLabel[];
  emailsSentToday: number;
  totalEmailsSent: number;
  warmupSpeed: WarmupSpeed;
  currentDailyLimit: number;
  nextDayLimit: number;
  daysToMaxLimit: number;
  authProvider: AuthProvider;
  targetAudience: TargetAudience;
  subscriptionId: string | null;
  planId: string | null;
  addonPlan: string | null;
  warmupStartDate: string;
  customWarmupPlan?: CustomWarmupPlan | null;
  /** From mailbox GET: ESP mix (e.g. { googleWorkspace: 30, gmail: 10, ... }) */
  warmupConfig?: { espBreakdown?: Record<string, number> } | null;
  /** From mailbox GET: IANA timezone */
  timezone?: string;
}

/** Warmup plan from API (strategy + today's value) */
export interface CustomWarmupPlan {
  enabled?: boolean;
  maxDailyLimit?: number;
  initialLimit?: number;
  dailyIncrease?: number;
  daysToMaxLimit?: number | null;
  warmupStrategy?: "progressive" | "random" | "flat";
  progressiveStartingVolume?: number;
  progressiveIncreaseBy?: number;
  progressiveMaxLimit?: number;
  randomMinLimit?: number;
  randomMaxLimit?: number;
  flatConstantEmails?: number;
  increasePerDay?: number;
  /** Today's sending limit / current value */
  currentCounter?: number;
}

/** B2B warmup settings collected before/during enabling warmup */
export interface B2BWarmupSettings {
  settingsMode: "default" | "manual";
  // Channel/Provider mix (%)
  gmailPercent?: number;
  googleWorkspacePercent?: number;
  microsoft365Percent?: number;
  yahooPercent?: number;
  outlookPercent?: number;
  zohoPercent?: number;
  // Reply To
  replyToEnabled?: boolean;
  replyToEmails?: string;
  // Warm-up strategy: "progressive" | "random" | "flat"
  warmupStrategy?: "progressive" | "random" | "flat";
  // Progressive: daily increase until max
  progressiveStartingVolume?: number;
  progressiveIncreaseBy?: number;
  progressiveMaxLimit?: number;
  // Random: min–max per day
  randomMinLimit?: number;
  randomMaxLimit?: number;
  // Flat: fixed emails per day
  flatConstantEmails?: number;
  // Legacy / derived
  increasePerDay?: number;
  currentCounter?: number;
  // Limits & Engagement
  dailyWarmupLimit?: number;
  replyRatePercent?: number;
  // Warmup Filter Tag
  customTag?: string;
  defaultTag?: string;
  // Custom Tracking Domain
  enableCustomTrackingDomain?: boolean;
  trackingDomainOrSubdomain?: string;
  weekdaysOnly?: boolean;
  /** When using custom schedule: 0=Sun, 1=Mon, ... 6=Sat. Omitted when using weekdaysOnly. */
  sendOnDays?: number[];
  readEmulation?: boolean;
  warmupCustomTrackingDomain?: boolean;
  // Select Tracking Domain (id or value)
  trackingDomainId?: string;
  // Timezone (IANA or display value)
  timezone?: string;
  // Engagement Rates
  openRatesPercent?: number;
  spamProtectionPercent?: number;
  markImportantPercent?: number;
  timeGapMinutes?: number;
  /** Daily delivery window: start (minutes from midnight, 0–1439). Default 0 = 12:00 AM. */
  deliveryWindowStartMinutes?: number;
  /** Daily delivery window: end (minutes from midnight, 1–1440). Default 1440 = 12:00 AM next day = 24h. */
  deliveryWindowEndMinutes?: number;
}

export interface ImapConfig {
  host: string;
  port: number;
  auth: {
    user: string;
    pass: string;
  };
  secure: boolean;
}

export interface SmtpConfig {
  host: string;
  port: number;
  auth: {
    user: string;
    pass: string;
  };
  secure: boolean;
}
export interface SmtpValidationResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

export interface DailyMetrics {
  opens: number;
  stars: number;
  replies: number;
  clicks: number;
  moved_primary: number;
  unspam: number;
  spam: number;
}

export interface TotalMetrics {
  total_opens: number;
  total_stars: number;
  total_replies: number;
  total_clicks: number;
  total_moved_primary: number;
  total_unspam: number;
  total_spam: number;
}

export interface CategoryPercentages {
  primary: number;
  updates: number;
  forums: number;
  promotions: number;
  spam: number;
  focused: number;
  other: number;
  junk: number;
  inbox: number;
}

export interface CategoryStats {
  primary: number;
  updates: number;
  forums: number;
  promotions: number;
  spam: number;
  focused: number;
  other: number;
  junk: number;
  inbox: number;
  percentages: CategoryPercentages;
}

export interface ProviderCategoryStats {
  totalEmails: number;
  categories: CategoryStats;
}

export interface CombinedStats {
  categories: CategoryStats;
}

export interface SenderDailyStats {
  totalEmails: number;
  combined: CombinedStats;
  gmail: ProviderCategoryStats;
  outlook: ProviderCategoryStats;
  workspace: ProviderCategoryStats;
  microsoft: ProviderCategoryStats;
  metrics?: DailyMetrics;
  categories?: CategoryStats;
}

export interface SenderTotalStats {
  totalEmails: number;
  combined: CombinedStats;
  gmail: ProviderCategoryStats;
  outlook: ProviderCategoryStats;
  workspace: ProviderCategoryStats;
  microsoft: ProviderCategoryStats;
  metrics?: TotalMetrics;
  categories?: CategoryStats;
  yahoo: ProviderCategoryStats;
}

export interface SenderMailboxStats {
  email: string;
  today: SenderDailyStats;
  total: SenderTotalStats;
}

export enum MailboxTemplateType {
  ALL_DAYS = "allDays",
  WEEKDAYS = "weekDays",
}

export enum DayOfWeek {
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
  SUNDAY = "sunday",
  ALL_DAYS = "allDays",
}

export interface TemplateConfiguration {
  templateIds: string[];
  lastUsedIndex: number;
}

export interface MailboxSettings {
  _id: string;
  mailboxId: string;
  templateType: MailboxTemplateType;
  templateConfigurations: Record<DayOfWeek, TemplateConfiguration>;
  useAssignedTemplates: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateMailboxSettings {
  templateType: MailboxTemplateType;
  templateConfigurations: Record<
    DayOfWeek,
    Pick<TemplateConfiguration, "templateIds">
  >;
  useAssignedTemplates?: boolean;
}

export interface WarmupStats {
  email: string;
  todayStats: {
    total: number;
    categories: {
      [key: string]: number;
    };
  };
  overallStats: {
    total: number;
    categories: {
      [key: string]: number;
    };
  };
}

export interface MailboxInteractions {
  opens: number;
  stars: number;
  replies: number;
  clicks: number;
  movedToPrimary: number;
  links_clicked: number;
  spam: number;
  inbox: number;
  unspamCount: number;
  engagement_rate: number;
  send: number;
  mailboxtemperature: number;
}

export interface CategoryStats {
  count: number;
  percentage: number;
}

export interface EmailPlacement {
  primary: CategoryStats;
  promotions: CategoryStats;
  updates: CategoryStats;
  forums: CategoryStats;
  spam: CategoryStats;
}

export interface DailyEmailPlacement {
  date: string;
  receivedTotal: number;
  inboxTotal: number;
  spamTotal: number;
  placement: EmailPlacement;
}

export interface DailyStats extends MailboxInteractions {
  date: string;
  gmailOpens: number;
  gmailStars: number;
  gmailEngagementRate: number;
  outlookOpens: number;
  outlookStars: number;
  outlookEngagementRate: number;
  yahooOpens: number;
  yahooStars: number;
  yahooEngagementRate: number;
  workspaceOpens: number;
  workspaceStars: number;
  workspaceEngagementRate: number;
  microsoftOpens: number;
  microsoftStars: number;
  microsoftEngagementRate: number;
  gmailReplies: number;
  outlookReplies: number;
  yahooReplies: number;
  gmailClicks: number;
  outlookClicks: number;
  yahooClicks: number;
  workspaceClicks: number;
  microsoftClicks: number;
}

export interface MailboxStatsResponse {
  emailPlacementByDate: DailyEmailPlacement[];
  dailyStats: DailyStats[];
  summary: {
    totalReceived: number;
    totalInbox: number;
    totalSpam: number;
    totalGmailReplies: number;
    totalOutlookReplies: number;
    todayrecived: number;
    todayinbox: number;
    todayspam: number;
  };
}

export type MailboxForPlan = Pick<
  MailboxType,
  "_id" | "email" | "planId" | "addonPlan" | "subscriptionId"
>;

// B2C Sender Types matching the API
export enum EspType {
  SENDGRID = 'sendgrid',
  MAILGUN = 'mailgun',
  AMAZON_SES = 'amazon_ses',
  POSTMARK = 'postmark',
  SPARKPOST = 'sparkpost',
  MAILJET = 'mailjet',
  MICROSOFT_365 = 'microsoft_365',
  GOOGLE_WORKSPACE = 'google_workspace',
  GMAIL = 'gmail',
  OUTLOOK = 'outlook',
  YAHOO = 'yahoo',
  AOL = 'aol',
  PROTON = 'proton',
  ZOHO = 'zoho',
  INBOX = 'inbox',
  FASTMAIL = 'fastmail',
  YANDEX = 'yandex',
  OTHER = 'other'
}

export enum ConnectionStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected'
}

export interface ISenderProfile {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  espType: EspType | AuthProvider | string; // Support both old and new enum
  warmup: boolean;
  companyId: string;
  targetAudience?: TargetAudience; // Optional for B2C senders
  createdAt: string;
  updatedAt: string;

  // Subscription and plan fields
  subscriptionId?: string | null;
  planId?: string | null;
  addonPlan?: string | null;
  
  // Seed and volume fields
  seedVolume?: number;
  seedAssign?: number;
  sendingVolume?: number;

  seedEspTypes?: string[];
  inboxGmail?: number;
  inboxOutlook?: number;
  inboxYahoo?: number;
  spamGmail?: number;
  spamOutlook?: number;
  spamYahoo?: number;
  inboxGoogleWorkspace?: number;
  inboxMicrosoft365?: number;
  spamGoogleWorkspace?: number;
  spamMicrosoft365?: number;
  inboxZoho?: number;
  spamZoho?: number;
  googleworkspaceSeeds?: number;
  microsoft365Seeds?: number;
  
  // Connection and labels
  connectionStatus?: ConnectionStatus | string;
  labels?: any;
  
  // Warmup status (using isWarmupEnabled for UI compatibility)
  isWarmupEnabled?: boolean;
  
  // Backward compatibility for existing code
  isActive?: boolean;
  authProvider?: AuthProvider;
  seedAssigned?: string;
  gmailSeeds?: number;
  outlookSeeds?: number;
  yahooSeeds?: number;
  googleWorkspaceSeeds?: number;
  microsoftWorkspaceSeeds?: number;
  seedSendingVolume?: string;
  openPercentages?: {
    inboxGmail?: number;
    inboxOutlook?: number;
    inboxYahoo?: number;
    inboxGoogleWorkspace?: number;
    inboxMicrosoftWorkspace?: number;
    spamGmail?: number;
    spamOutlook?: number;
    spamYahoo?: number;
    spamGoogleWorkspace?: number;
    spamMicrosoftWorkspace?: number;
    inboxZoho?: number;
    spamZoho?: number;
  };
}

// DTOs for creating and updating senders
export interface CreateB2cSenderDto {
  firstName: string;
  lastName: string;
  email: string;
  espType: EspType | string;
  warmup?: boolean;
  planId?: string;
  subscriptionId?: string;
  seedVolume?: number;
  seedAssign?: number;
  labels?: string[];
  connectionStatus?: ConnectionStatus;
  sendingVolume?: number;
  isWarmupEnabled?: boolean;
}

export interface UpdateB2cSenderDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  espType?: EspType | string;
  warmup?: boolean;
  planId?: string;
  subscriptionId?: string;
  seedVolume?: number;
  seedAssign?: number;
  labels?: string[];
  connectionStatus?: ConnectionStatus;
  sendingVolume?: number;
  isWarmupEnabled?: boolean;
}

export interface FilterB2cSenderDto {
  labels?: string[];
  subscriptionIds?: string[];
  planIds?: string[];
  connectionStatus?: ConnectionStatus;
  minSendingVolume?: number;
  maxSendingVolume?: number;
  minSeedVolume?: number;
  maxSeedVolume?: number;
  minSeedAssign?: number;
  maxSeedAssign?: number;
  page?: number;
  limit?: number;
}

export interface ListB2cSenderResponse {
  data: ISenderProfile[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}

export interface AwsApiPayload {
  awsAccessKey: string;
  awsSecret: string;
  awsRegion: string;

}

export interface AwsSmtpPayload {
  firstName: string;
  lastName: string;
  email: string;
  awsAccessKey: string;
  awsSecret: string;
  smtpHost: string;
  smtpPort: number;
  smtpTls: string;
  smtpPassword: string;
  smtpUsername: string;
}

export interface SendGridApiPayload {
  apiKey: string;
}
export interface VerifySendGridApiPayload {
  apiKey: string;
}

export interface SendGridSender {
  id: number;
  nickname: string;
  from: {
    email: string;
    name: string;
  };
  reply_to: {
    email: string;
    name: string;
  };
  address: string;
  address_2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  verified: {
    status: boolean;
    reason: string | null;
  };
  updated_at: number;
  created_at: number;
  locked: boolean;
}
export interface VerifySendGridApiResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

export interface SendGridSmtpPayload {
  apiKey: string;
  email: string;
  smtpHost: string;
  smtpPort: number;
  encryptionMethod: string;
  smtpPassword: string;
  smtpUsername: string;
}

export interface AzureSmtpPayload {
  firstName: string;
  lastName: string;
  email: string;
  smtpHost: string;
  smtpPort: number;
  smtpTls: string;
  smtpPassword: string;
  smtpUsername: string;
  imapHost: string;
  imapPort: number;
  imapPassword: string;
  imapTls: boolean;
}
